import { Container, Button, Row, Col } from 'react-bootstrap';
import { useMsal } from '@azure/msal-react';
import { loginRequest } from '../../authConfig';

import '../../styles/App.css';

const CONTACT_EMAIL = 'contact@arkognize.com';

const mailto = (subject) => `mailto:${CONTACT_EMAIL}?subject=${encodeURIComponent(subject)}`;

const TRIAL_MAILTO = mailto('Request a pilot of Arkognize');
const PARTNER_MAILTO = mailto('Partnership with Arkognize');

const BENEFITS = [
    { title: 'Improve project quality', desc: 'Catch clashes, standard violations, and missing data before they reach the site.' },
    { title: 'Accelerate delivery', desc: 'Spend less time on manual model checks and more time on design and coordination.' },
    { title: 'Reduce costs', desc: 'Every issue found in the model is one less change order, delay, or rework on site.' },
];

const STEPS = [
    { num: '01', title: 'Connect your models', desc: 'Link Arkognize to your Autodesk projects. It reads your models and understands their content.' },
    { num: '02', title: 'Identify issues', desc: 'Run clash detection and governance rules across disciplines to find what needs attention.' },
    { num: '03', title: 'Resolve together', desc: 'Share clear results with your team and track each issue until it is fixed.' },
];

const PARTNER_PERKS = [
    'Early access to new features',
    'Direct line to our product team',
    'Influence on our roadmap',
    'Special terms for early partners',
];

export const Landing = () => {
    const { instance } = useMsal();

    const handleSignIn = () => {
        instance.loginRedirect(loginRequest).catch((e) => console.error(e));
    };

    return (
        <div className="landing-page">
            {/* ── Hero ── */}
            <section className="landing-hero">
                <div className="hero-glow hero-glow-left" />
                <div className="hero-glow hero-glow-right" />
                <Container className="hero-content text-center">
                    <img src="/logo.svg" alt="Arkognize" className="hero-logo" />
                    <div className="hero-badge">BIM Governance · Coordination · Collaboration</div>
                    <h1 className="hero-title">
                        Understand Your Models.<br />
                        <span className="hero-title-accent">Resolve Issues Faster.</span>
                    </h1>
                    <p className="hero-subtitle">
                        Arkognize is an intelligent platform for BIM governance, coordination, and
                        collaboration. It understands your models, identifies issues, and streamlines
                        their resolution to improve project quality, accelerate delivery, and reduce costs.
                    </p>
                    <div className="hero-actions">
                        <Button className="cta-btn-primary" size="lg" href={TRIAL_MAILTO}>
                            Request a Pilot
                        </Button>
                        <Button className="cta-btn-secondary" size="lg" onClick={handleSignIn}>
                            Sign In
                        </Button>
                    </div>
                </Container>
                <div className="hero-grid-overlay" />
            </section>

            {/* ── Benefits ── */}
            <section className="features-section">
                <Container>
                    <div className="section-header text-center">
                        <p className="section-label">WHY ARKOGNIZE</p>
                        <h2 className="section-title">Better models, better projects</h2>
                    </div>
                    <Row className="g-4">
                        {BENEFITS.map((benefit) => (
                            <Col key={benefit.title} md={4}>
                                <div className="feature-card">
                                    <h4 className="feature-title">{benefit.title}</h4>
                                    <p className="feature-desc">{benefit.desc}</p>
                                </div>
                            </Col>
                        ))}
                    </Row>
                </Container>
            </section>

            {/* ── How it works ── */}
            <section className="how-section">
                <Container>
                    <div className="section-header text-center">
                        <p className="section-label">HOW IT WORKS</p>
                        <h2 className="section-title">From model to resolution in three steps</h2>
                    </div>
                    <Row className="steps-row g-4 justify-content-center">
                        {STEPS.map((step) => (
                            <Col key={step.num} md={4}>
                                <div className="step-card">
                                    <div className="step-num">{step.num}</div>
                                    <h4 className="step-title">{step.title}</h4>
                                    <p className="step-desc">{step.desc}</p>
                                </div>
                            </Col>
                        ))}
                    </Row>
                </Container>
            </section>

            {/* ── Work with us ── */}
            <section className="landing-cta-section">
                <Container>
                    <div className="section-header text-center">
                        <p className="section-label">WORK WITH US</p>
                        <h2 className="cta-heading">See Arkognize on your own projects</h2>
                        <p className="section-subtitle">
                            We are working with a small group of design firms, contractors, and BIM
                            teams. Join them and help shape the future of BIM coordination.
                        </p>
                    </div>
                    <Row className="g-4 justify-content-center">
                        <Col md={6} lg={5}>
                            <div className="offer-card">
                                <h3 className="offer-title">Try Arkognize</h3>
                                <p className="offer-desc">
                                    Run a free pilot on one of your live projects. We help you connect
                                    your models and set up rules, and you see the results within days.
                                </p>
                                <Button className="cta-btn-primary" href={TRIAL_MAILTO}>
                                    Request a Pilot
                                </Button>
                            </div>
                        </Col>
                        <Col md={6} lg={5}>
                            <div className="offer-card">
                                <h3 className="offer-title">Become a Partner</h3>
                                <ul className="offer-list">
                                    {PARTNER_PERKS.map((perk) => (
                                        <li key={perk}>{perk}</li>
                                    ))}
                                </ul>
                                <Button className="cta-btn-secondary" href={PARTNER_MAILTO}>
                                    Talk to Us
                                </Button>
                            </div>
                        </Col>
                    </Row>
                    <p className="cta-subtext text-center offer-contact">
                        Or write to us at <a href={`mailto:${CONTACT_EMAIL}`}>{CONTACT_EMAIL}</a>
                    </p>
                </Container>
            </section>

            <footer className="landing-footer">
                <Container className="text-center">
                    <img src="/logo.svg" alt="Arkognize" className="footer-logo" />
                    <span className="footer-sep">·</span>
                    <span className="footer-tagline">BIM Governance, Coordination & Collaboration</span>
                    <span className="footer-sep">·</span>
                    <a href={`mailto:${CONTACT_EMAIL}`} className="footer-contact">
                        {CONTACT_EMAIL}
                    </a>
                </Container>
            </footer>
        </div>
    );
};

export default Landing;
